/* ==========================================================================
   voxel-manta.js — the build half, vendored verbatim apart from exports.

   The turtle's flipper is a rigid wing that twists. A manta's pectoral is a
   flexible MEMBRANE, and that is the whole difference: the motion is a
   travelling wave that runs outward along the span and backward along the
   chord at the same time, so the tip lags the root and the trailing edge
   lags the leading edge. Freeze either lag at zero and the wing becomes a
   rigid plate hinged at the shoulder — the animal stops rippling and starts
   flapping.

       phase(s,c) = stroke - spanLag*s - chordLag*c
       dy         = amp * s^flex * sin(phase)
       twist      = tw  * s^1.15 * sin(phase + lead)

   Everything is a function of the voxel's own baked span and chord fraction,
   so a whole disc of several thousand voxels deforms per-vertex on the GPU
   with nothing rebuilt on the CPU. MANTA_WAVE_GLSL below is that evaluator.
   ========================================================================== */
'use strict';

const TAU = Math.PI * 2;
const clamp = (v, a, b) => (v < a ? a : v > b ? b : v);
const lerp = (a, b, t) => a + (b - a) * t;
function sstep(e0, e1, x) { const t = clamp((x - e0) / (e1 - e0), 0, 1); return t * t * (3 - 2 * t); }
function mulberry32(a) {
  return function () {
    a |= 0; a = (a + 0x6D2B79F5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
function makeNoise(seed) {
  const r = mulberry32(seed >>> 0), P = new Float32Array(1024);
  for (let i = 0; i < 1024; i++) P[i] = r();
  function h(x, y, z) {
    let n = (x * 374761393 + y * 668265263 + z * 1442695041) | 0;
    n = ((n ^ (n >> 13)) * 1274126177) | 0;
    return P[(n >>> 0) & 1023];
  }
  return function (x, y, z) {
    const xi = Math.floor(x), yi = Math.floor(y), zi = Math.floor(z);
    const xf = x - xi, yf = y - yi, zf = z - zi;
    const u = xf * xf * (3 - 2 * xf), v = yf * yf * (3 - 2 * yf), w = zf * zf * (3 - 2 * zf);
    const a = lerp(h(xi, yi, zi), h(xi + 1, yi, zi), u), b = lerp(h(xi, yi + 1, zi), h(xi + 1, yi + 1, zi), u);
    const c = lerp(h(xi, yi, zi + 1), h(xi + 1, yi, zi + 1), u), d = lerp(h(xi, yi + 1, zi + 1), h(xi + 1, yi + 1, zi + 1), u);
    return lerp(lerp(a, b, v), lerp(c, d, v), w);
  };
}
function fbm(nz, x, y, z, oct, gain) {
  let a = 1, f = 1, s = 0, n = 0; gain = gain || 0.5;
  for (let i = 0; i < oct; i++) { s += a * (nz(x * f, y * f, z * f) - 0.5) * 2; n += a; a *= gain; f *= 2.03; }
  return s / n;
}
const mixc = (a, b, t) => [lerp(a[0], b[0], t), lerp(a[1], b[1], t), lerp(a[2], b[2], t)];

/* parts, which the shader uses to pick a deform */
const P_DISC = 0, P_LOBE_L = 1, P_LOBE_R = 2, P_TAIL = 3;
/* roles, which the fragment shader uses to pick pigment */
const R_TOP = 0, R_BOT = 1, R_SPOT = 2, R_PATCH = 3, R_EYE = 4, R_MOUTH = 5, R_GILL = 6, R_EDGE = 7;

function buildManta(st) {
  const t0 = (typeof performance !== 'undefined' ? performance : Date).now();
  const vox = st.vox, seed = st.seed, span = st.span, depth = st.depth, tailF = st.tail, tipr = st.tipr;
  const rnd = mulberry32(seed >>> 0), nz = makeNoise(seed * 7919 + 11);

  const px = [], py = [], pz = [], part = [], ps = [], pu = [], pv = [], role = [], cr = [], cg = [], cbl = [], shd = [];
  let nDisc = 0, nLobe = 0, nTail = 0, nSpot = 0;

  /* ---- disc outline -----------------------------------------------------
     Leading edge sweeps hard back; trailing edge is concave, sweeping
     forward off the body then back out to a pointed tip. */
  const W = 1.10 * span;                             /* half span */
  const zLE = s => 0.46 - 0.88 * Math.pow(s, 1.30);
  const zTE = s => -0.52 + 0.30 * Math.pow(s, 1.55) - 0.34 * Math.pow(s, 4.0);
  /* Tip cap. A POINTED tip has the two edges meeting at an angle, so the
     chord closes linearly and the last few percent of span is under a voxel
     wide — which comes out as a ragged spike rather than an edge. A ROUNDED
     tip closes with a vertical tangent, which is a sqrt falloff. */
  const TP = lerp(24.0, 4.0, tipr);
  const tipCap = s => Math.sqrt(Math.max(0, 1 - Math.pow(s, TP)));
  const TH = 0.032 * depth;
  const thick = (s, cn) => {
    const spanT = Math.pow(Math.max(0, 1 - Math.pow(s, 1.55)), 0.85);
    const chordT = Math.pow(Math.max(0, 1 - cn * cn), 0.52) * (1 + 0.22 * cn);
    /* the central body carries most of the animal's mass; the wings are a
       membrane a few voxels thick */
    const bulge = 1 + 3.1 * Math.exp(-Math.pow(s / 0.255, 2));
    return TH * spanT * chordT * bulge;
  };
  /* A membrane thinner than one voxel cannot be sampled: at the outer wing
     the disc is ~0.011 deep against a 0.028 lattice, so only a third of the
     columns catch anything and the wing comes out perforated. The SILHOUETTE
     still tapers — that is set by the outline, not the thickness — so
     flooring the depth costs nothing and makes the wing solid.

     The floor has to exceed TWO lattice spacings, not one: a band 1.25 voxels
     wide still contains only a single sample point, so the sheet stays one
     voxel thick and dissolves when seen edge-on. */
  const insideDisc = (x, y, z) => {
    const s = Math.abs(x) / W; if (s > 1) return false;
    const le = zLE(s), te = zTE(s);
    const zc = (le + te) * 0.5, hc = (le - te) * 0.5 * tipCap(s);
    if (hc < 1e-4) return false;
    const cn = (z - zc) / hc; if (Math.abs(cn) > 1) return false;
    const t = Math.max(thick(s, cn), vox * 2.1);
    return y <= t * 0.62 && y >= -t * 0.38;          /* belly flatter than back */
  };

  /* ---- belly spots: a manta's ventral pattern is individual ---- */
  const SPOTS = [];
  {
    const n = 16 + (rnd() * 16 | 0);
    for (let i = 0; i < n; i++)
      SPOTS.push({ x: (rnd() - 0.5) * W * 0.95, z: -0.42 + rnd() * 0.80, r: 0.030 + rnd() * 0.062, w: 0.55 + rnd() * 0.5 });
    nSpot = n;
  }
  const spotAt = (x, z) => {
    let v = 0;
    for (const s of SPOTS) v = Math.max(v, s.w * sstep(s.r, s.r * 0.35, Math.hypot(x - s.x, (z - s.z) * 1.25)));
    return v;
  };

  const TOPD = [0.130, 0.145, 0.172], TOPL = [0.300, 0.336, 0.372];
  const BOT = [0.855, 0.872, 0.860], EDGE = [0.030, 0.034, 0.044];

  for (let x = -W - vox; x <= W + vox; x += vox)
    for (let z = -0.60; z <= 0.52; z += vox)
      for (let y = -0.14; y <= 0.16; y += vox) {
        if (!insideDisc(x, y, z)) continue;
        if (insideDisc(x + vox, y, z) && insideDisc(x - vox, y, z) && insideDisc(x, y + vox, z) &&
          insideDisc(x, y - vox, z) && insideDisc(x, y, z + vox) && insideDisc(x, y, z - vox)) continue;
        const s = Math.abs(x) / W, le = zLE(s), te = zTE(s);
        const zc = (le + te) * 0.5, hc = Math.max((le - te) * 0.5 * tipCap(s), 1e-4);
        const cn = (z - zc) / hc;
        const t = Math.max(thick(s, cn), vox * 2.1), up = t > 1e-5 ? y / t : 0;
        let R, c;
        if (up > 0.10) {                               /* dorsal */
          R = R_TOP;
          c = mixc(TOPD, TOPL, sstep(-0.4, 0.7, fbm(nz, x * 3.1, y * 3.1, z * 3.1, 3)) * 0.7);
          /* two clean shoulder blotches, angled like the chevron a manta wears */
          const bx = (Math.abs(x) - 0.285), bz = (z - 0.185);
          const rot = bx * 0.87 + bz * 0.49, rop = -bx * 0.49 + bz * 0.87;
          const pq = sstep(1.0, 0.30, Math.hypot(rot / 0.175, rop / 0.085));
          if (pq > 0.45) R = R_PATCH;
          c = mixc(c, [0.720, 0.760, 0.790], pq * 0.85);
        } else if (up < -0.10) {                       /* ventral */
          const sv = spotAt(x, z);
          R = sv > 0.35 ? R_SPOT : R_BOT;
          c = mixc(BOT, [0.115, 0.125, 0.140], clamp(sv, 0, 1) * 0.92);
          /* five gill slits either side of the midline */
          const gi = (z + 0.26) / 0.088;
          if (Math.abs(x) > 0.10 && Math.abs(x) < 0.30 && z > -0.30 && z < 0.14 && (gi - Math.floor(gi)) < 0.34) {
            R = R_GILL; c = [0.235, 0.245, 0.255];
          }
          /* mouth: a wide terminal slot right at the front */
          if (z > le - 0.075 && Math.abs(x) < 0.235) { R = R_MOUTH; c = [0.045, 0.048, 0.058]; }
        } else {                                       /* rim */
          R = R_EDGE; c = EDGE;
        }
        /* eyes sit on the sides of the head, outboard of the cephalic lobes */
        for (const sg of [1, -1]) {
          if (Math.hypot((x - sg * 0.245) / 1.05, (y - 0.010) / 0.9, (z - 0.315) / 1.05) < 0.042) {
            R = R_EYE; c = [0.030, 0.030, 0.038];
          }
        }
        px.push(x); py.push(y); pz.push(z);
        part.push(P_DISC); ps.push(s); pu.push(cn); pv.push(zc); role.push(R);
        cr.push(c[0]); cg.push(c[1]); cbl.push(c[2]);
        shd.push(0.90 + 0.20 * nz(x * 19, y * 19, z * 19));
        nDisc++;
      }

  /* Fill an ellipse with voxels. Sampling a bounding square and culling by
     radius yields NOTHING once the ellipse drops below one lattice cell —
     the four corners all sit at r*sqrt(2), outside the ellipse — which is
     exactly how the back half of a tail vanishes and leaves it detached.
     The centreline is always emitted as a floor. */
  function ring(rx, ry, cb) {
    const NX = Math.max(1, Math.ceil(rx * 2 / vox)), NY = Math.max(1, Math.ceil(ry * 2 / vox));
    let n = 0;
    for (let j = 0; j <= NX; j++) for (let k = 0; k <= NY; k++) {
      const a = (j / NX - 0.5) * 2 * rx, b = (k / NY - 0.5) * 2 * ry;
      if (Math.hypot(a / Math.max(rx, 1e-6), b / Math.max(ry, 1e-6)) > 1.001) continue;
      cb(a, b); n++;
    }
    if (n === 0) cb(0, 0);
  }

  /* ---- cephalic lobes: the two paddles either side of the mouth --------- */
  const LOBE_L = 0.32;
  function lobe(idx, rootX) {
    const NS = Math.max(8, Math.round(LOBE_L / vox));
    for (let i = 0; i <= NS; i++) {
      const s = i / NS;
      /* a flattened paddle, not a rod: wide across, thin through */
      const rw = 0.030 * (1 - 0.30 * s) * (0.60 + 0.55 * Math.sin(Math.pow(s, 0.65) * Math.PI)) + 0.006;
      const rt = rw * 0.42;
      ring(rw, rt, (a, b) => {
        let c = [0.085, 0.094, 0.112];
        c = mixc(c, [0.640, 0.665, 0.670], sstep(0.70, 1.0, s) * 0.60);
        px.push(rootX); py.push(0.004); pz.push(0.350);   /* rooted inside the disc */
        part.push(idx); ps.push(s); pu.push(a); pv.push(b); role.push(R_TOP);
        cr.push(c[0]); cg.push(c[1]); cbl.push(c[2]);
        shd.push(0.90 + 0.20 * nz(s * 40, a * 40, b * 40));
        nLobe++;
      });
    }
  }
  lobe(P_LOBE_L, -0.175); lobe(P_LOBE_R, 0.175);

  /* ---- tail: a stiff whip trailing off the back of the disc ------------- */
  {
    const TL = 0.95 * tailF;
    if (TL > vox * 3) {
      const NS = Math.max(8, Math.round(TL / vox));
      for (let i = 0; i <= NS; i++) {
        const s = i / NS;
        /* the +0.008 floor is what keeps the far tail from vanishing */
        const rr = 0.034 * (1 - 0.72 * s) + 0.008;
        ring(rr, rr * 0.85, (a, b) => {
          px.push(0); py.push(0.010); pz.push(-0.440);    /* rooted inside the disc */
          part.push(P_TAIL); ps.push(s * TL); pu.push(a); pv.push(b); role.push(R_EDGE);
          cr.push(0.058); cg.push(0.062); cbl.push(0.076);
          shd.push(0.92 + 0.16 * nz(s * 44, a * 44, b * 44));
          nTail++;
        });
      }
    }
  }

  const n = px.length;
  return {
    n, nDisc, nLobe, nTail, nSpot, vox, W,
    x: new Float32Array(px), y: new Float32Array(py), z: new Float32Array(pz),
    part: new Float32Array(part), s: new Float32Array(ps),
    u: new Float32Array(pu), v: new Float32Array(pv),
    role: new Float32Array(role), shade: new Float32Array(shd),
    cr: new Float32Array(cr), cg: new Float32Array(cg), cbl: new Float32Array(cbl),
    ms: (typeof performance !== 'undefined' ? performance : Date).now() - t0
  };
}

/* --------------------------------------------------------------------------
   The wing wave.

   One phase expression drives everything. Subtracting spanLag*s makes the
   tip trail the root; subtracting chordLag*c makes the trailing edge trail
   the leading edge. The section also pitches, a quarter cycle out of step,
   which is what turns the flap into thrust rather than just stirring water.
   -------------------------------------------------------------------------- */
const MANTA_WAVE_GLSL = `
uniform float uStroke,uAmp,uFlex,uSpanLag,uChordLag,uTwist,uLead,uW,uBank,uLobe,uTailAmp;
attribute float aPart,aS,aRole,aShade;
attribute vec2 aUV;
attribute vec3 aCol;

float wingPhase(float s,float cn){
  /* cn runs +1 at the leading edge to -1 at the trailing edge */
  return uStroke - uSpanLag*s - uChordLag*(0.5-cn*0.5);
}
void discPos(float s,vec2 uv,vec3 rest,out vec3 P){
  float cn=uv.x, zc=uv.y;
  float ph=wingPhase(s,cn);
  float amp=uAmp*(1.0+sign(rest.x)*uBank*0.38);
  float dy=amp*uW*pow(s,uFlex)*sin(ph);
  /* the section pitches as it sweeps; without the lead it is a flat plate */
  float tw=uTwist*pow(s,1.15)*sin(ph+uLead);
  float rz=rest.z-zc, ry=rest.y;
  float ct=cos(tw), st=sin(tw);
  P=vec3(rest.x, ry*ct-rz*st+dy, zc+ry*st+rz*ct);
}
void lobePos(float part,float t,vec2 co,vec3 root,out vec3 P){
  float side=(part<1.5)?-1.0:1.0;
  /* Rolled up when cruising, unfurled to a forward scoop when feeding. The
     spiral is a circular arc, integrated in closed form so the tip can wrap
     right round without any stepping. */
  float A=mix(-3.40,-0.50,uLobe);
  float p0=mix(0.35,-0.10,uLobe);
  float As=(A>=0.0?1.0:-1.0)*max(abs(A),0.02);
  float len=0.32;
  float f1=p0+As*t;
  float sp=0.30;
  vec3 E1=normalize(vec3(side*sin(sp),0.0,cos(sp)));
  vec3 E3=vec3(0.0,1.0,0.0);
  vec3 E2=normalize(cross(E3,E1));
  vec3 arc=E1*((len/As)*(sin(f1)-sin(p0)))+E3*((len/As)*(cos(p0)-cos(f1)));
  vec3 T=E1*cos(f1)+E3*sin(f1);
  vec3 B=cross(T,E2);
  P=root+arc+E2*co.x+B*co.y;
}
void tailPos(float t,vec2 co,vec3 root,out vec3 P){
  /* the tail is stiff and mostly trails, lagging the body by a long phase */
  float ph=uStroke-1.9-t*1.35;
  float sway=uTailAmp*t*t*sin(ph);
  P=vec3(root.x+co.x+sway, root.y+co.y+sway*0.35, root.z-t);
}
`;

export {
  buildManta, MANTA_WAVE_GLSL,
  P_DISC, P_LOBE_L, P_LOBE_R, P_TAIL,
  R_TOP, R_BOT, R_SPOT, R_PATCH, R_EYE, R_MOUTH, R_GILL, R_EDGE,
};
export default { buildManta, MANTA_WAVE_GLSL };
